import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  Plus, X, Loader2, Headphones, AlertTriangle, Clock,
  ArrowUpCircle, Shield,
} from 'lucide-react';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable, useDraggable, DragOverlay,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface ServiceTicket {
  id: string;
  number: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  category: string;
  contact?: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  escalationLevel: number;
  escalatedAt?: string | null;
}

const COLUMNS: { key: string; label: string; color: string }[] = [
  { key: 'open', label: 'Open', color: 'border-blue-400' },
  { key: 'in_progress', label: 'In Progress', color: 'border-yellow-400' },
  { key: 'on_hold', label: 'On Hold', color: 'border-orange-400' },
  { key: 'resolved', label: 'Resolved', color: 'border-green-400' },
  { key: 'closed', label: 'Closed', color: 'border-gray-400' },
  { key: 'escalated', label: 'Escalated', color: 'border-red-400' },
];

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-red-50 text-red-700',
};

const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const CATEGORIES = ['installation', 'repair', 'warranty', 'maintenance', 'complaint'];

// SLA limits in hours by priority
const SLA_HOURS: Record<string, number> = {
  critical: 4,
  high: 8,
  medium: 24,
  low: 48,
};

interface EscalationRule {
  id?: string;
  name: string;
  triggerType?: string;
  conditions?: Record<string, unknown>;
  condition?: Record<string, unknown>;
  action: string;
  active?: boolean;
  priority?: number;
}

function formatElapsed(ms: number): string {
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    return `${days}d ${hours}h`;
  }
  return `${totalHours}h ${minutes}m`;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'BREACHED';
  return formatElapsed(ms);
}

type SlaStatus = 'green' | 'yellow' | 'red';

function getSlaInfo(ticket: ServiceTicket): { elapsed: string; remaining: string; status: SlaStatus; remainingMs: number } {
  const now = Date.now();
  const created = new Date(ticket.createdAt).getTime();
  const elapsedMs = now - created;
  const slaMs = (SLA_HOURS[ticket.priority] ?? 24) * 60 * 60 * 1000;
  const remainingMs = slaMs - elapsedMs;
  const pctUsed = elapsedMs / slaMs;

  let status: SlaStatus = 'green';
  if (remainingMs <= 0) {
    status = 'red';
  } else if (pctUsed > 0.75) {
    status = 'yellow';
  }

  return {
    elapsed: formatElapsed(elapsedMs),
    remaining: formatRemaining(remainingMs),
    status,
    remainingMs,
  };
}

const SLA_STATUS_STYLES: Record<SlaStatus, string> = {
  green: 'bg-green-50 text-green-700 border-green-200',
  yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

function conditionToString(condition: Record<string, unknown>): string {
  const parts: string[] = [];
  if (condition.priority) parts.push(`Priority: ${condition.priority}`);
  if (condition.noResponseHours) parts.push(`No response > ${condition.noResponseHours}h`);
  if (condition.notResolvedHours) parts.push(`Not resolved > ${condition.notResolvedHours}h`);
  if (condition.event) parts.push(`Event: ${condition.event}`);
  if (condition.sameCustomerTickets) parts.push(`Same customer tickets >= ${condition.sameCustomerTickets} in ${condition.withinDays}d`);
  if (condition.status) parts.push(`Status: ${condition.status}`);
  return parts.join(', ') || JSON.stringify(condition);
}

function actionLabel(action: string): string {
  switch (action) {
    case 'escalate': return 'Escalate Ticket';
    case 'notify_manager': return 'Notify Manager';
    case 'notify_owner': return 'Notify Owner';
    case 'close': return 'Auto-Close';
    default: return action;
  }
}

/* ── @dnd-kit helper components ── */

function TicketDroppableArea({ columnKey, children }: { columnKey: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: columnKey });
  return (
    <div
      ref={setNodeRef}
      className={`flex-1 space-y-2 p-3 overflow-y-auto max-h-[calc(100vh-280px)] transition-colors ${isOver ? 'bg-[var(--saffron-50)] ring-2 ring-[var(--ink-3)] ring-inset rounded-b-xl' : ''}`}
    >
      {children}
    </div>
  );
}

function DraggableTicketCard({
  ticket,
  isActive,
  onNavigate,
  onEscalate,
  escalatingId,
}: {
  ticket: ServiceTicket;
  isActive: boolean;
  onNavigate: () => void;
  onEscalate: () => void;
  escalatingId: string | null;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: ticket.id });
  const sla = getSlaInfo(ticket);
  const isTerminal = ticket.status === 'closed' || ticket.status === 'resolved';
  const canEscalate = ticket.status !== 'closed' && ticket.status !== 'resolved' && ticket.escalationLevel < 2;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing touch-none ${isActive || isDragging ? 'opacity-40' : ''}`}
      onClick={onNavigate}
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-mono text-gray-400">{ticket.number}</span>
        {/* SLA badge */}
        {!isTerminal && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${SLA_STATUS_STYLES[sla.status]}`}>
            <Clock size={10} />
            {sla.status === 'red' ? 'BREACHED' : sla.remaining}
          </span>
        )}
      </div>
      <h4 className="mb-2 text-sm font-medium text-gray-900 leading-tight line-clamp-2">
        {ticket.title}
      </h4>
      {/* SLA Timer */}
      {!isTerminal && (
        <div className="mb-2 flex items-center gap-1.5 text-[10px] text-gray-400">
          <Clock size={10} />
          <span>Elapsed: {sla.elapsed}</span>
          <span className="text-gray-300">|</span>
          <span>SLA: {SLA_HOURS[ticket.priority] ?? 24}h</span>
        </div>
      )}
      <div className="mb-2 flex items-center gap-2">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[ticket.priority || 'medium'] || PRIORITY_COLORS.medium}`}>
          {ticket.priority ? ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) : 'Medium'}
        </span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {ticket.category ? ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1) : 'General'}
        </span>
        {ticket.escalationLevel > 0 && (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
            L{ticket.escalationLevel}
          </span>
        )}
      </div>
      {ticket.contact && (
        <p className="mb-2 text-xs text-gray-500">
          {joinPersonName(ticket.contact.firstName, ticket.contact.lastName)}
        </p>
      )}
      {/* Escalate button */}
      {canEscalate && (
        <button
          onClick={(e) => { e.stopPropagation(); onEscalate(); }}
          disabled={escalatingId === ticket.id}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
        >
          {escalatingId === ticket.id ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <ArrowUpCircle size={12} />
          )}
          Escalate
        </button>
      )}
    </div>
  );
}

export function ServiceTicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<ServiceTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create Ticket Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [ticketAccounts, setTicketAccounts] = useState<{ id: string; name: string }[]>([]);
  const [ticketWarranties, setTicketWarranties] = useState<{ id: string; product?: { name: string } | null; serialNumber?: string }[]>([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'repair',
    contactId: '',
    accountId: '',
    warrantyId: '',
  });

  // Escalation Rules Modal
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [escalationRules, setEscalationRules] = useState<EscalationRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);

  // Escalating state
  const [escalatingId, setEscalatingId] = useState<string | null>(null);

  // Timer refresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60_000); // re-render every minute
    return () => clearInterval(interval);
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/service-tickets');
      setTickets(res.data.data?.serviceTickets ?? res.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load service tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    api.get('/contacts').then(res => setContacts(res.data.data?.contacts ?? res.data.data ?? [])).catch(() => {});
    api.get('/accounts').then(res => setTicketAccounts(res.data.data?.accounts ?? res.data.data ?? [])).catch(() => {});
    api.get('/warranties').then(res => setTicketWarranties(res.data.data?.warranties ?? res.data.data ?? [])).catch(() => {});
  }, [fetchTickets]);

  const ticketsByStatus = (status: string) => tickets.filter(t => t.status === status);

  // @dnd-kit drag-and-drop
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDndDragStart = (event: DragStartEvent) => {
    setActiveTicketId(event.active.id as string);
  };

  const handleDndDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTicketId(null);
    if (!over) return;

    const ticketId = active.id as string;
    const targetStatus = over.id as string;

    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket || ticket.status === targetStatus) return;

    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: targetStatus } : t));

    try {
      await api.patch(`/service-tickets/${ticketId}/status`, { status: targetStatus });
    } catch (err: any) {
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: ticket.status } : t));
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const activeTicket = activeTicketId ? tickets.find(t => t.id === activeTicketId) : null;

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/service-tickets', {
        title: form.title,
        description: form.description || undefined,
        priority: form.priority,
        category: form.category,
        contactId: form.contactId || undefined,
        accountId: form.accountId || undefined,
        warrantyId: form.warrantyId || undefined,
      });
      setShowModal(false);
      setForm({ title: '', description: '', priority: 'medium', category: 'repair', contactId: '', accountId: '', warrantyId: '' });
      fetchTickets();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  const handleEscalate = async (ticketId: string) => {
    try {
      setEscalatingId(ticketId);
      await api.post(`/service-tickets/${ticketId}/escalate`);
      fetchTickets();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to escalate ticket');
    } finally {
      setEscalatingId(null);
    }
  };

  const fetchEscalationRules = async () => {
    try {
      setLoadingRules(true);
      const res = await api.get('/service/escalation-rules');
      const payload = res.data.data?.escalationRules ?? res.data.data ?? [];
      setEscalationRules(Array.isArray(payload) ? payload : []);
    } catch {
      setEscalationRules([]);
    } finally {
      setLoadingRules(false);
    }
  };

  const handleShowRules = () => {
    setShowRulesModal(true);
    fetchEscalationRules();
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Tickets</h1>
          <p className="text-sm text-gray-500">Manage customer service and support tickets</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleShowRules}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Shield size={16} />
            Escalation Rules
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            Create Ticket
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading tickets...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchTickets} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Headphones size={40} className="mb-2" />
          <p className="text-sm">No service tickets found</p>
          <p className="text-xs">Create your first ticket to get started</p>
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDndDragStart} onDragEnd={handleDndDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {COLUMNS.map(col => {
              const colTickets = ticketsByStatus(col.key);
              return (
                <div
                  key={col.key}
                  className={`flex w-64 flex-shrink-0 flex-col rounded-xl border-t-2 ${col.color} border border-gray-200 bg-gray-50`}
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between bg-white px-4 py-3 rounded-t-xl border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">{col.label}</h3>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {colTickets.length}
                      </span>
                    </div>
                  </div>

                  {/* Ticket Cards */}
                  <TicketDroppableArea columnKey={col.key}>
                    {colTickets.length === 0 ? (
                      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-8 text-gray-400">
                        <p className="text-xs">No tickets</p>
                      </div>
                    ) : (
                      colTickets.map(ticket => (
                        <DraggableTicketCard
                          key={ticket.id}
                          ticket={ticket}
                          isActive={activeTicketId === ticket.id}
                          onNavigate={() => navigate(`/service-tickets/${ticket.id}`)}
                          onEscalate={() => handleEscalate(ticket.id)}
                          escalatingId={escalatingId}
                        />
                      ))
                    )}
                  </TicketDroppableArea>
                </div>
              );
            })}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeTicket ? (
              <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg w-64 opacity-90 rotate-2">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-400">{activeTicket.number}</span>
                </div>
                <h4 className="mb-2 text-sm font-medium text-gray-900 leading-tight line-clamp-2">
                  {activeTicket.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[activeTicket.priority] || PRIORITY_COLORS.medium}`}>
                    {activeTicket.priority ? activeTicket.priority.charAt(0).toUpperCase() + activeTicket.priority.slice(1) : 'Medium'}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {activeTicket.category ? activeTicket.category.charAt(0).toUpperCase() + activeTicket.category.slice(1) : 'General'}
                  </span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Create Ticket Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Create Ticket</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    {PRIORITIES.map(p => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Customer (Contact)</label>
                <select
                  value={form.contactId}
                  onChange={(e) => setForm({ ...form, contactId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                >
                  <option value="">Select Contact</option>
                  {contacts.map(c => (
                    <option key={c.id} value={c.id}>{joinPersonName(c.firstName, c.lastName)}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                  <select
                    value={form.accountId}
                    onChange={(e) => setForm({ ...form, accountId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Select Account</option>
                    {ticketAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Warranty</label>
                  <select
                    value={form.warrantyId}
                    onChange={(e) => setForm({ ...form, warrantyId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Select Warranty</option>
                    {ticketWarranties.map(w => (
                      <option key={w.id} value={w.id}>{w.product?.name || 'Unknown'} {w.serialNumber ? `(${w.serialNumber})` : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.title}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Escalation Rules Modal */}
      {showRulesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowRulesModal(false)}>
          <div className="mx-4 w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-[var(--ink-0)]" />
                <h2 className="text-lg font-semibold text-gray-900">Escalation Rules</h2>
              </div>
              <button onClick={() => setShowRulesModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-4">
              {loadingRules ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
                  <span className="ml-2 text-sm text-gray-500">Loading rules...</span>
                </div>
              ) : escalationRules.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <AlertTriangle size={28} className="mb-2" />
                  <p className="text-sm">No escalation rules configured</p>
                  <p className="text-xs">Run the database seed to create default rules.</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Rule Name</th>
                        <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Condition</th>
                        <th className="px-4 py-2.5 text-xs font-semibold text-gray-500">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {escalationRules.map((rule, idx) => (
                        <tr key={idx} className="border-b border-gray-100 last:border-0">
                          <td className="px-4 py-2.5 font-medium text-gray-800">{rule.name}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{conditionToString(rule.conditions ?? rule.condition ?? {})}</td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              rule.action === 'escalate' ? 'bg-red-50 text-red-700' :
                              rule.action === 'close' ? 'bg-gray-100 text-gray-600' :
                              'bg-blue-50 text-blue-700'
                            }`}>
                              {actionLabel(rule.action)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-gray-200 px-6 py-3">
              <button
                onClick={() => setShowRulesModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
